import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Loader2, Info } from "lucide-react";
import { streamPost } from "@/lib/api";
import { useLang } from "@/i18n";

const GOAL_VALUES = ["weight-loss", "muscle-gain", "general"];

const AIPlan = () => {
  const { lang, t } = useLang();
  const [form, setForm] = useState({ name: "", age: "", goal: "general" });
  const [plan, setPlan] = useState("");
  const [generating, setGenerating] = useState(false);

  const inputCls =
    "w-full rounded-lg border border-zinc-700 bg-ink px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors duration-200 focus:border-volt focus:ring-1 focus:ring-volt";
  const labelCls = "mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-widest text-zinc-400";

  const generate = (e) => {
    e.preventDefault();
    if (generating) return;
    setPlan("");
    setGenerating(true);
    streamPost("/ai/plan", { name: form.name.trim(), age: parseInt(form.age, 10), goal: form.goal, lang }, {
      onDelta: (d) => setPlan((p) => p + d),
      onDone: () => setGenerating(false),
      onError: () => {
        setGenerating(false);
        setPlan(t.error_generic);
      },
    });
  };

  return (
    <section id="ai-plan" className="relative border-t border-zinc-800/60 bg-surface/40 py-24 sm:py-32" data-testid="ai-plan-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <p className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.3em] text-volt" data-testid="ai-plan-kicker">
            — {t.ai_plan_kicker}
          </p>
          <h2 className="font-display text-4xl font-extrabold uppercase tracking-tight text-white sm:text-5xl" data-testid="ai-plan-title">
            {t.ai_plan_title}
          </h2>
          <p className="mt-4 max-w-md text-base text-zinc-400 sm:text-lg" data-testid="ai-plan-subtitle">{t.ai_plan_sub}</p>

          <form onSubmit={generate} className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="ai-plan-form">
            <div>
              <label className={labelCls} htmlFor="ai-name">{t.form_name} *</label>
              <input id="ai-name" data-testid="ai-plan-name-input" required minLength={2} maxLength={80}
                className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Rahul Sharma" />
            </div>
            <div>
              <label className={labelCls} htmlFor="ai-age">{t.form_age} *</label>
              <input id="ai-age" data-testid="ai-plan-age-input" required type="number" min={10} max={90}
                className={inputCls} value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} placeholder="24" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>{t.form_goal} *</label>
              <div className="grid grid-cols-3 gap-2" data-testid="ai-plan-goal-group">
                {GOAL_VALUES.map((v, i) => (
                  <button
                    key={v}
                    type="button"
                    data-testid={`ai-plan-goal-${v}-button`}
                    onClick={() => setForm({ ...form, goal: v })}
                    className={`rounded-lg border px-2 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors duration-200 ${
                      form.goal === v ? "border-volt bg-volt/10 text-volt" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                    }`}
                  >
                    {t.goals[i]}
                  </button>
                ))}
              </div>
            </div>
            <button
              data-testid="ai-plan-generate-button"
              type="submit"
              disabled={generating}
              className="flex items-center justify-center gap-2 rounded-full bg-volt py-4 font-display text-base font-bold uppercase tracking-widest text-ink transition-all duration-200 hover:bg-volt-hover hover:scale-[1.01] disabled:opacity-60 sm:col-span-2"
            >
              {generating ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
              {generating ? t.ai_plan_generating : t.ai_plan_generate}
            </button>
          </form>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="flex min-h-[24rem] flex-col rounded-2xl border border-volt/30 bg-panel p-7 volt-glow"
          data-testid="ai-plan-output"
        >
          <p className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-widest text-volt">
            <Sparkles className="h-4 w-4" /> Fitnastic AI
          </p>
          <div className="mt-4 flex-1 whitespace-pre-wrap text-sm leading-relaxed text-zinc-200" data-testid="ai-plan-text">
            {plan || (generating ? t.ai_plan_generating : <span className="text-zinc-600">…</span>)}
          </div>
          <p className="mt-4 flex items-start gap-2 border-t border-zinc-800 pt-4 text-[11px] text-zinc-500" data-testid="ai-plan-disclaimer">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-volt" />
            {t.ai_plan_disclaimer}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default AIPlan;
